import { useRef, useState, useEffect } from 'preact/hooks'

import Icon from '@/components/snippets/icon/icon'

import { SearchInput } from 'libs/searchspring/components/quick-search/SearchInput'
import { QuickSearchDropdown, ViewResultsButton } from 'libs/searchspring/components/quick-search/QuickSearchDropdown'
import { SearchspringAutocompleteProvider } from 'libs/searchspring/fetchers/autocomplete'

function QuickSearch({ id = 'quick-search' }) {
  const searchInputRef = useRef()
  const [inputValue, setInputValue] = useState()
  const [query, setQuery] = useState()
  const closeButtonRef = useRef()
  const modalRef = useRef()
  const [redirect, setRedirect] = useState(false)
  const [hasResults, setHasResults] = useState(false)
  const [totalResults, setTotalResults] = useState(0)

  const handleFocusInput = () => {
    setTimeout(() => {
      searchInputRef.current.focus()
    }, 0)
  }

  const handleClose = (event) => {
    event.preventDefault()
    modalRef.current?.handleHide()
  }

  const handleUpdateValue = (event) => {
    setInputValue(event.target.value)
  }

  const resetInputValue = () => {
    setInputValue('')
    searchInputRef.current.value = ''
  }

  useEffect(() => {
    const timeout = setTimeout(() => {
      setQuery(inputValue)
    }, 400)
    return () => {
      clearTimeout(timeout)
    }
  }, [inputValue])

  return (
    <x-modal className="group/search flex items-center h-full z-0" data-id={id} data-body-lock ref={modalRef}>
      <button
        className="flex items-center gap-x-0.625 link-fade"
        data-modal-parent={id}
        data-modal-role="open"
        onClick={handleFocusInput}
        aria-label="Toggle search"
      >
        <div className="flex w-[16px] h-[17px] children:size-full">
          <Icon id="magnifying-glass" />
        </div>
        <span className="hidden xl:block text-s font-bold">Search</span>
      </button>
      <div
        className="flex flex-col fixed inset-tl w-full h-fit z-100 -translate-y-full group-data-visible/search:translate-y-0 transition-all"
      >
        <form action="/search" method="get" className="w-full h-[72px] lg:h-[106px] px-margin border-b border-neutral-medium bg-secondary">
          <div className="flex items-center justify-between gap-x-1.5 h-full">
            <div className="grow flex items-center gap-x-0.75">
              <div className="shrink-0 w-[22px] h-[23px] children:size-full">
                <Icon id="magnifying-glass" />
              </div>
              <SearchInput ref={searchInputRef} redirect={redirect} onInput={handleUpdateValue} />
            </div>
            <div className={'shrink-0 flex items-center justify-end gap-x-margin h-full'}>
              {inputValue ? (
                <button className="w-fit text-b text-neutral-secondary text-line link-fade" onClick={resetInputValue} type="reset" aria-label="Clear search">
                  Clear<span className="hidden md:inline"> Input</span>
                </button>
              ) : null}
              {hasResults && !redirect && (
                <ViewResultsButton query={query} totalResults={totalResults} />
              )}
              <div className="flex items-center w-fit h-full pl-margin border-l border-neutral-medium">
                <button
                  className="flex size-[11px] link-fade children:size-full"
                  data-modal-parent={id}
                  data-modal-role="close"
                  ref={closeButtonRef}
                  onClick={handleClose}
                  aria-label="Close modal"
                >
                  <Icon id="close" />
                </button>
              </div>
            </div>
          </div>
        </form>
        <SearchspringAutocompleteProvider runOnMount={false} resultsPerPage={4}>
          <QuickSearchDropdown
            query={query}
            redirect={redirect}
            setRedirect={setRedirect}
            onResultsUpdate={({ hasResults, totalResults }) => {
              setHasResults(hasResults)
              setTotalResults(totalResults)
            }}
          />
        </SearchspringAutocompleteProvider>
      </div>
      <button
        className="fixed inset-tl size-full bg-neutral-black/60 opacity-0 group-data-visible/search:opacity-100 pointer-events-none group-data-visible/search:pointer-events-auto cursor-n-resize z-90 transition-all"
        data-modal-parent={id}
        data-modal-role="close"
        aria-label="Close modal"
      ></button>
    </x-modal>
  )
}

export function WrappedQuickSearch({ id }) {
  return (
    <SearchspringAutocompleteProvider runOnMount={false}>
      <QuickSearch id={id} />
    </SearchspringAutocompleteProvider>
  )
}
